import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { CategoryPicker, categoryLabel } from '../components/CategoryPicker'
import { Screen } from '../components/Layout'
import { Button, Card, ErrorBanner, Field, Spinner, inputClass } from '../components/ui'
import { longDate, money, parseAmount, timestamp } from '../lib/format'
import {
  useAddExpenseSplit,
  useCategories,
  useDeleteExpense,
  useExpense,
  useMe,
  useUpdateExpense,
} from '../lib/queries'
import type { Expense, MemberSummary } from '../lib/types'

/**
 * View and edit one expense (requirement 8).
 *
 * `expectedRevision` goes with every save. When the server rejects it the form
 * keeps the user's typing and shows what changed underneath -- discarding the
 * edit because someone else touched the row first would be the worse failure.
 *
 * What the edit does to an attached split is the server's decision, not this
 * page's: a settled share is frozen, an equal share is re-halved, a hand-set
 * share is left alone and the payer absorbs the difference. The note under the
 * amount field says so, because otherwise the numbers look arbitrary.
 */
export function ExpenseDetailPage() {
  const { expenseId } = useParams<{ expenseId: string }>()
  const navigate = useNavigate()
  const detail = useExpense(expenseId)
  const taxonomy = useCategories()
  const me = useMe()
  const update = useUpdateExpense(expenseId ?? '')
  const remove = useDeleteExpense(expenseId ?? '')

  const [editing, setEditing] = useState(false)
  const [date, setDate] = useState('')
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState<{ categoryId: string; subCategoryId?: string }>({
    categoryId: 'other',
  })
  const [merchant, setMerchant] = useState('')
  const [note, setNote] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Seed the form from the server copy, and re-seed after a successful save so
  // the fields reflect what was actually stored (the server may have coerced an
  // archived category to `other`).
  const loaded = detail.data?.expense
  useEffect(() => {
    if (!loaded) return
    setDate(loaded.date)
    setAmount(String(loaded.amountMinor))
    setCategory({
      categoryId: loaded.categoryId,
      ...(loaded.subCategoryId ? { subCategoryId: loaded.subCategoryId } : {}),
    })
    setMerchant(loaded.merchant ?? '')
    setNote(loaded.note ?? '')
  }, [loaded])

  if (detail.isLoading) {
    return (
      <Screen title="支出">
        <Spinner />
      </Screen>
    )
  }
  if (!detail.data) {
    return (
      <Screen title="支出">
        <ErrorBanner error={detail.error} onRetry={() => void detail.refetch()} />
        <Link to="/" className="mt-4 block text-sm text-accent">
          回到本月
        </Link>
      </Screen>
    )
  }

  const current = detail.data.expense
  const amountMinor = parseAmount(amount, current.currency)
  // The live split, not merely the first ref: a cancelled split keeps its ref,
  // and treating it as live is what hid the way to split the expense again.
  const liveSplit = detail.data.splits.find((s) => !s.voided)
  const pairName = me.data?.members.find((m) => m.pairId === liveSplit?.pairId)?.displayName

  function save(): void {
    if (amountMinor === undefined) return
    const patch: Parameters<typeof update.mutate>[0] = { expectedRevision: current.revision }
    if (date !== current.date) patch.date = date
    if (amountMinor !== current.amountMinor) patch.amountMinor = amountMinor
    if (category.categoryId !== current.categoryId) patch.categoryId = category.categoryId
    if ((category.subCategoryId ?? null) !== (current.subCategoryId ?? null)) {
      // `null` clears the sub-category; omitting the field would leave the old
      // one attached to a different main.
      patch.subCategoryId = category.subCategoryId ?? null
    }
    if (merchant.trim() !== (current.merchant ?? '')) patch.merchant = merchant.trim()
    if (note.trim() !== (current.note ?? '')) patch.note = note.trim()

    const changed = Object.keys(patch).length > 1
    if (!changed) {
      setEditing(false)
      return
    }
    update.mutate(patch, { onSuccess: () => setEditing(false) })
  }

  return (
    <Screen
      title="支出"
      action={
        <button type="button" className="text-sm text-accent" onClick={() => setEditing(!editing)}>
          {editing ? '取消' : '編輯'}
        </button>
      }
    >
      <div className="space-y-4">
        <ErrorBanner
          error={update.error ?? remove.error}
          {...(update.error ? { onRetry: () => void detail.refetch() } : {})}
        />

        {!editing ? (
          <>
            <Card className="p-4">
              <p className="tnum text-3xl font-semibold">
                {money(current.amountMinor, current.currency)}
              </p>
              {current.isSplit ? (
                <p className="tnum mt-1 text-sm text-owed">
                  你負擔 {money(current.myShareMinor, current.currency)}
                </p>
              ) : null}
              <p className="mt-1 text-sm text-muted">{longDate(current.date)}</p>
              {current.feeMinor !== undefined ? (
                <p className="mt-2 text-xs text-muted">
                  含手續費 {money(current.feeMinor, current.currency)}
                  {current.originalCurrency
                    ? ` · 原幣 ${current.originalAmount} ${current.originalCurrency}`
                    : ''}
                </p>
              ) : null}
            </Card>

            <Card className="divide-y divide-ink-line/40">
              <Row label="分類">
                {categoryLabel(taxonomy.data, current.categoryId, current.subCategoryId)}
              </Row>
              {current.merchant ? <Row label="店家">{current.merchant}</Row> : null}
              {current.note ? <Row label="備註">{current.note}</Row> : null}
              <Row label="來源">{current.source === 'import' ? '帳單匯入' : '手動輸入'}</Row>
              <Row label="最後更新">{timestamp(current.updatedAt)}</Row>
            </Card>

            {liveSplit ? (
              <Card className="p-4">
                <p className="text-sm text-muted">分帳</p>
                <p className="mt-1">
                  與 {pairName ?? '成員'} 分帳 · 對方負擔{' '}
                  <span className="tnum">{money(liveSplit.shareMinor, current.currency)}</span>
                  {liveSplit.settled ? '（已結清）' : ''}
                </p>
                <Link
                  to={`/members/${liveSplit.pairId}`}
                  className="mt-2 inline-block text-sm text-accent"
                >
                  查看分帳明細
                </Link>
              </Card>
            ) : (
              <AddSplit expense={current} members={me.data?.members ?? []} />
            )}

            {confirmDelete ? (
              <Card className="space-y-3 p-4">
                <p className="text-sm">
                  刪除這筆支出？
                  {liveSplit
                    ? '已結清的分帳金額會保留在對帳紀錄中，未結清的會一併取消。'
                    : ''}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    className="flex-1"
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate(current.revision, {
                        onSuccess: () => navigate('/', { replace: true }),
                      })
                    }
                  >
                    {remove.isPending ? '刪除中…' : '確定刪除'}
                  </Button>
                  <Button variant="secondary" className="flex-1" onClick={() => setConfirmDelete(false)}>
                    保留
                  </Button>
                </div>
              </Card>
            ) : (
              <Button variant="ghost" className="w-full" onClick={() => setConfirmDelete(true)}>
                刪除這筆支出
              </Button>
            )}
          </>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              save()
            }}
          >
            <Card className="space-y-4 p-4">
              <Field
                label="金額"
                {...(liveSplit
                  ? {
                      hint: '改金額會重算未結清的分帳；已結清的金額不會變動，自訂金額也會保留。',
                    }
                  : {})}
              >
                <input
                  className={`${inputClass} tnum text-2xl`}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
              <Field label="日期">
                <input
                  type="date"
                  className={inputClass}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </Field>
            </Card>

            {taxonomy.data ? (
              <CategoryPicker
                taxonomy={taxonomy.data}
                categoryId={category.categoryId}
                {...(category.subCategoryId ? { subCategoryId: category.subCategoryId } : {})}
                onChange={setCategory}
              />
            ) : null}

            <Card className="space-y-4 p-4">
              <Field label="店家">
                <input
                  className={inputClass}
                  value={merchant}
                  onChange={(e) => setMerchant(e.target.value)}
                />
              </Field>
              <Field label="備註">
                <input
                  className={inputClass}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </Field>
            </Card>

            <Button
              type="submit"
              className="w-full"
              disabled={amountMinor === undefined || update.isPending}
            >
              {update.isPending ? '儲存中…' : '儲存變更'}
            </Button>
          </form>
        )}
      </div>
    </Screen>
  )
}

/**
 * Split an expense that was saved alone (requirement 5).
 *
 * The usual story is an expense typed in, or imported, before anyone remembered
 * it was shared -- so the way in is on the expense itself, not only at creation.
 * Same copy and same rule as the add screen: empty share means half, and the
 * server, not this form, works out what half is.
 */
function AddSplit({ expense, members }: { expense: Expense; members: MemberSummary[] }) {
  const add = useAddExpenseSplit(expense.expenseId)
  const [open, setOpen] = useState(false)
  const [pairId, setPairId] = useState('')
  const [share, setShare] = useState('')

  if (members.length === 0) return null

  const shareMinor = share.trim() === '' ? undefined : parseAmount(share, expense.currency)
  const shareInvalid = share.trim() !== '' && shareMinor === undefined
  const shareTooBig = shareMinor !== undefined && shareMinor > expense.amountMinor
  const chosen = pairId || members[0]!.pairId

  if (!open) {
    return (
      <Button variant="secondary" className="w-full" onClick={() => setOpen(true)}>
        分帳
      </Button>
    )
  }

  return (
    <Card className="space-y-3 p-4">
      <ErrorBanner error={add.error} />
      <Field label="與誰分帳">
        <select
          className={inputClass}
          value={chosen}
          onChange={(e) => setPairId(e.target.value)}
        >
          {members.map((member) => (
            <option key={member.pairId} value={member.pairId}>
              {member.displayName}
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="對方負擔金額（選填）"
        hint={`留空則各付一半：對方 ${money(Math.floor(expense.amountMinor / 2), expense.currency)}。`}
      >
        <input
          className={`${inputClass} tnum`}
          inputMode="decimal"
          value={share}
          placeholder="平分"
          onChange={(e) => setShare(e.target.value)}
        />
      </Field>
      {shareTooBig ? (
        <p className="text-xs text-owing">對方負擔金額不能超過總金額。</p>
      ) : null}
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={add.isPending || shareInvalid || shareTooBig}
          onClick={() =>
            add.mutate(
              {
                pairId: chosen,
                expectedRevision: expense.revision,
                ...(shareMinor !== undefined ? { shareMinor } : {}),
              },
              { onSuccess: () => setOpen(false) },
            )
          }
        >
          {add.isPending ? '分帳中…' : '確定分帳'}
        </Button>
        <Button variant="secondary" className="flex-1" onClick={() => setOpen(false)}>
          取消
        </Button>
      </div>
    </Card>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-4 py-3">
      <span className="text-sm text-muted">{label}</span>
      <span className="min-w-0 truncate text-right text-sm">{children}</span>
    </div>
  )
}
